import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import MobileMetricsCards from './components/MobileMetricsCards';
import ActiveProjectsList from './components/ActiveProjectsList';
import ResourceStatusPanel from './components/ResourceStatusPanel';
import MobileQuickActions from './components/MobileQuickActions';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import { supabase } from '../../lib/supabase';
import mobileConfigService from '../../services/mobileConfigService';

const detectPlatform = () => {
  const ua = navigator?.userAgent?.toLowerCase() || '';
  if (/iphone|ipad|ipod/?.test(ua)) return 'ios';
  if (/android/?.test(ua)) return 'android';
  return 'ios'; // default for desktop preview
};

const MobileDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [projects, setProjects] = useState([]);
  const [resources, setResources] = useState([]);
  const [bids, setBids] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [visibility, setVisibility] = useState({
    metrics: true,
    active_projects: true,
    resource_status: true,
  });
  const platform = detectPlatform();

  const fetchVisibilityConfig = useCallback(async () => {
    try {
      const config = await mobileConfigService?.getScreenVisibility(platform);
      if (config && typeof config === 'object') {
        setVisibility(prev => ({
          ...prev,
          metrics: config?.metrics !== false,
          active_projects: config?.active_projects !== false,
          resource_status: config?.resource_status !== false,
        }));
      }
    } catch (err) {
      console.error('Failed to load mobile config:', err);
    }
  }, [platform]);

  const fetchData = useCallback(async () => {
    try {
      const [projectsRes, resourcesRes, bidsRes, prospectsRes] = await Promise.all([
        supabase?.from('projects')?.select('id, name, status, start_date, end_date, location, progress')?.order('created_at', { ascending: false }),
        supabase?.from('resources')?.select('id, name, category, status')?.order('name'),
        supabase?.from('bids')?.select('id, status, created_at')?.order('created_at', { ascending: false }),
        supabase?.from('prospects')?.select('id, status')?.order('created_at', { ascending: false }),
      ]);

      setProjects(projectsRes?.data || []);
      setResources(resourcesRes?.data || []);
      setBids(bidsRes?.data || []);
      setProspects(prospectsRes?.data || []);
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
    }
  }, []);

  const loadAll = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    await Promise.all([fetchVisibilityConfig(), fetchData()]);
    setLoading(false);
    setRefreshing(false);
  }, [fetchVisibilityConfig, fetchData]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Compute metrics
  const activeProjects = projects?.filter(p => p?.status === 'In Progress' || p?.status === 'Planning');
  const openBids = bids?.filter(b => b?.status === 'Draft' || b?.status === 'Submitted');
  const availableResources = resources?.filter(r => r?.status?.toLowerCase() === 'available');
  const qualifiedProspects = prospects?.filter(p => p?.status === 'Qualified' || p?.status === 'Proposal');

  const metrics = {
    activeProjects: activeProjects?.length,
    activeProjectsSubtitle: `${projects?.filter(p => p?.status === 'Planning')?.length} in planning`,
    openBids: openBids?.length,
    openBidsSubtitle: `${bids?.filter(b => b?.status === 'Submitted')?.length} submitted`,
    resourcesAvailable: `${availableResources?.length}/${resources?.length}`,
    resourcesSubtitle: `${resources?.length - availableResources?.length} assigned`,
    prospects: qualifiedProspects?.length,
    prospectsSubtitle: `${prospects?.filter(p => p?.status === 'Proposal')?.length} in proposal`,
  };

  return (
    <MainLayout>
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="sticky top-0 z-40 bg-background/95 backdrop-blur border-b border-border px-4 py-3">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-bold text-foreground">Mobile Dashboard</h1>
              <p className="text-xs text-muted-foreground capitalize">{platform} · Live data</p>
            </div>
            <button
              onClick={() => loadAll(true)}
              disabled={refreshing}
              className="w-9 h-9 rounded-full bg-muted flex items-center justify-center"
            >
              <Icon
                name="RefreshCw"
                size={16}
                className={`text-muted-foreground ${refreshing ? 'animate-spin' : ''}`}
              />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-4 py-4 space-y-4 pb-24">
          {/* Platform badge */}
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 bg-muted rounded-full px-3 py-1">
              <Icon name={platform === 'ios' ? 'Smartphone' : 'Tablet'} size={12} className="text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                {platform === 'ios' ? 'iOS' : 'Android'} View
              </span>
            </div>
            {refreshing && (
              <span className="text-xs text-muted-foreground">Refreshing...</span>
            )}
          </div>

          {/* Metrics Section */}
          {visibility?.metrics && (
            <section>
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Key Metrics</h2>
              <MobileMetricsCards metrics={metrics} loading={loading} />
            </section>
          )}

          {/* Active Projects Section */}
          {visibility?.active_projects && (
            <section>
              <ActiveProjectsList projects={projects} loading={loading} />
            </section>
          )}

          {/* Resource Status Section */}
          {visibility?.resource_status && (
            <section>
              <ResourceStatusPanel resources={resources} loading={loading} />
            </section>
          )}

          {/* All sections hidden notice */}
          {!visibility?.metrics && !visibility?.active_projects && !visibility?.resource_status && !loading && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
                <Icon name="EyeOff" size={28} className="text-muted-foreground" />
              </div>
              <h3 className="text-base font-semibold text-foreground mb-1">No sections visible</h3>
              <p className="text-sm text-muted-foreground max-w-xs">
                All dashboard sections are hidden for {platform === 'ios' ? 'iOS' : 'Android'}. Update settings in Mobile App Configuration.
              </p>
              <button
                onClick={() => navigate('/mobile-app-configuration')}
                className="mt-4 text-sm font-medium text-accent underline"
              >
                Go to Configuration
              </button>
            </div>
          )}
        </div>

        {/* Floating Quick Actions */}
        <MobileQuickActions />

        {/* Bottom Navigation */}
        <MobileBottomNav />
      </div>
    </MainLayout>
  );
};

export default MobileDashboard;
